import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from '@shared/guards/ProtectedRoute/ProtectedRoute';
import { Role } from '@shared/enums';
import { isClubsRedeemEnabled } from '@/shared/config/features';

const Main = React.lazy(() => import('@pages/Main/Main').then((res) => ({ default: res.Main })));
const Players = React.lazy(() => import('@pages/Players/Players').then((res) => ({ default: res.Players })));
const Profile = React.lazy(() => import('@pages/Players/id/Profile').then((res) => ({ default: res.Profile })));
const Tournaments = React.lazy(() =>
    import('@/pages/Tournaments/Tournaments').then((res) => ({ default: res.Tournaments }))
);
const Tournament = React.lazy(() =>
    import('@/pages/Tournaments/id/Tournament').then((res) => ({ default: res.Tournament }))
);
const SignIn = React.lazy(() => import('@pages/Auth/SignIn').then((res) => ({ default: res.SignIn })));
const SignUp = React.lazy(() => import('@pages/Auth/SignUp').then((res) => ({ default: res.SignUp })));
const ResetPassword = React.lazy(() =>
    import('@pages/Auth/ResetPassword').then((res) => ({ default: res.ResetPassword }))
);
const ResetPasswordForm = React.lazy(() =>
    import('@pages/Auth/ResetPasswordForm').then((res) => ({ default: res.ResetPasswordForm }))
);
const Activation = React.lazy(() => import('@pages/Auth/Activation').then((res) => ({ default: res.Activation })));
const About = React.lazy(() => import('@pages/About/About').then((res) => ({ default: res.About })));
const ThrowError = React.lazy(() => import('@pages/ThrowError').then((res) => ({ default: res.ThrowError })));
const Viewer = React.lazy(() => import('@pages/Viewer/Viewer').then((res) => ({ default: res.Viewer })));
const Server = React.lazy(() => import('@pages/Server/Server').then((res) => ({ default: res.Server })));
const Statistics = React.lazy(() =>
    import('@pages/Statistics/Statistics').then((res) => ({ default: res.Statistics }))
);
const Games = React.lazy(() => import('@pages/Games/Games').then((res) => ({ default: res.Games })));
const Admin = React.lazy(() => import('@pages/Admin/Admin').then((res) => ({ default: res.Admin })));
const NewsEdit = React.lazy(() => import('@pages/Admin/NewsEdit/NewsEdit').then((res) => ({ default: res.NewsEdit })));
const User = React.lazy(() => import('@pages/User/User').then((res) => ({ default: res.User })));
const PageNotFind = React.lazy(() =>
    import('@pages/PageNotFind/PageNotFind').then((res) => ({ default: res.PageNotFind }))
);
const Demo = React.lazy(() => import('@pages/Server/Demo/Demo').then((res) => ({ default: res.Demo })));
const BroadcastGame = React.lazy(() =>
    import('@pages/Broadcast/id/BroadcastGame').then((res) => ({ default: res.BroadcastGame }))
);
const BroadcastPaymentReturn = React.lazy(() =>
    import('@pages/BroadcastPaymentReturn/BroadcastPaymentReturn').then((res) => ({
        default: res.BroadcastPaymentReturn,
    }))
);
const Broadcasts = React.lazy(() =>
    import('@pages/Broadcasts/Broadcasts').then((res) => ({ default: res.Broadcasts }))
);
const NewsView = React.lazy(() => import('@pages/News/id/NewsView').then((res) => ({ default: res.NewsView })));
const NewsList = React.lazy(() => import('@pages/News/NewsList/NewsList').then((res) => ({ default: res.NewsList })));
const ArticlesList = React.lazy(() =>
    import('@pages/Articles/ArticlesList/ArticlesList').then((res) => ({ default: res.ArticlesList }))
);
const ArticleView = React.lazy(() =>
    import('@pages/Articles/id/ArticleView').then((res) => ({ default: res.ArticleView }))
);

const Sandbox = React.lazy(() => import('@pages/Sandbox/Sandbox').then((res) => ({ default: res.Sandbox })));

const NewServerGame = React.lazy(() =>
    import('@pages/Server/NewGame/NewServerGame').then((res) => ({ default: res.NewServerGame }))
);

const Donate = React.lazy(() => import('@pages/Donate/Donate').then((res) => ({ default: res.Donate })));
const DonateVotes = React.lazy(() =>
    import('@pages/Donate/id/DonateVotes').then((res) => ({ default: res.DonateVotes }))
);
const ClubVoucherPurchase = React.lazy(() =>
    import('@pages/ClubVoucherPurchase/ClubVoucherPurchase').then((res) => ({ default: res.ClubVoucherPurchase }))
);
const ClubVoucherRedeem = React.lazy(() =>
    import('@pages/ClubVoucherRedeem/ClubVoucherRedeem').then((res) => ({ default: res.ClubVoucherRedeem }))
);

const isListNewsEnable = process.env.ENABLE_LIST_NEWS === 'true';
const isClubsEnable = process.env.ENABLE_CLUBS === 'true';

export const Router: React.FC = () => (
    <Routes>
        <Route
            path="/"
            element={
                <Suspense fallback={<>...</>}>
                    <Main />
                </Suspense>
            }
        />
        <Route
            path="/players"
            element={
                <Suspense fallback={<>...</>}>
                    <Players />
                </Suspense>
            }
        />
        <Route
            path="/players/:id"
            element={
                <Suspense fallback={<>...</>}>
                    <Profile />
                </Suspense>
            }
        />
        <Route
            path="/tournaments"
            element={
                <Suspense fallback={<>...</>}>
                    <Tournaments />
                </Suspense>
            }
        />
        <Route
            path="/tournaments/:id"
            element={
                <Suspense fallback={<>...</>}>
                    <Tournament />
                </Suspense>
            }
        />
        <Route
            path="/sign-in"
            element={
                <Suspense fallback={<>...</>}>
                    <SignIn />
                </Suspense>
            }
        />
        <Route
            path="/sign-up"
            element={
                <Suspense fallback={<>...</>}>
                    <SignUp />
                </Suspense>
            }
        />
        <Route
            path="/reset-password"
            element={
                <Suspense fallback={<>...</>}>
                    <ResetPassword />
                </Suspense>
            }
        />
        <Route
            path="/reset-password/:resetToken"
            element={
                <Suspense fallback={<>...</>}>
                    <ResetPasswordForm />
                </Suspense>
            }
        />
        <Route
            path="/sign-up/:code"
            element={
                <Suspense fallback={<>...</>}>
                    <Activation />
                </Suspense>
            }
        />
        <Route
            path="/about"
            element={
                <Suspense fallback={<>...</>}>
                    <About />
                </Suspense>
            }
        />
        <Route
            path="/throw-error"
            element={
                <Suspense fallback={<>...</>}>
                    <ThrowError />
                </Suspense>
            }
        />
        <Route
            path="/viewer/:id"
            element={
                <Suspense fallback={<>...</>}>
                    <Viewer />
                </Suspense>
            }
        />
        <Route
            path="/server"
            element={
                <Suspense fallback={<>...</>}>
                    <Server />
                </Suspense>
            }
        />
        <Route
            path="/server/:id"
            element={
                <Suspense fallback={<>...</>}>
                    <NewServerGame />
                </Suspense>
            }
        />
        <Route
            path="/statistics"
            element={
                <Suspense fallback={<>...</>}>
                    <Statistics />
                </Suspense>
            }
        />
        <Route
            path="/games"
            element={
                <Suspense fallback={<>...</>}>
                    <Games />
                </Suspense>
            }
        />
        <Route
            path="/admin"
            element={
                <ProtectedRoute allowedRoles={[Role.Admin]} fallbackRoute="/">
                    <Suspense fallback={<>...</>}>
                        <Admin />
                    </Suspense>
                </ProtectedRoute>
            }
        />
        <Route
            path="/admin/news/create"
            element={
                <ProtectedRoute allowedRoles={[Role.Admin]} fallbackRoute="/">
                    <Suspense fallback={<>...</>}>
                        <NewsEdit />
                    </Suspense>
                </ProtectedRoute>
            }
        />
        <Route
            path="/admin/news/:id"
            element={
                <ProtectedRoute allowedRoles={[Role.Admin]} fallbackRoute="/">
                    <Suspense fallback={<>...</>}>
                        <NewsEdit />
                    </Suspense>
                </ProtectedRoute>
            }
        />
        <Route
            path="/user"
            element={
                <ProtectedRoute allowedRoles={[Role.User]}>
                    <Suspense fallback={<>...</>}>
                        <User />
                    </Suspense>
                </ProtectedRoute>
            }
        />
        <Route
            path="/demo"
            element={
                <Suspense fallback={<>...</>}>
                    <Demo />
                </Suspense>
            }
        />
        <Route
            path="/broadcast/:id"
            element={
                <Suspense fallback={<>...</>}>
                    <BroadcastGame />
                </Suspense>
            }
        />
        <Route
            path="/payments/broadcast/:id/return"
            element={
                <Suspense fallback={<>...</>}>
                    <BroadcastPaymentReturn />
                </Suspense>
            }
        />
        <Route
            path="/broadcasts"
            element={
                <Suspense fallback={<>...</>}>
                    <Broadcasts />
                </Suspense>
            }
        />
        {isListNewsEnable && (
            <Route
                path="/news"
                element={
                    <Suspense fallback={<>...</>}>
                        <NewsList />
                    </Suspense>
                }
            />
        )}
        {isListNewsEnable && (
            <Route
                path="/articles"
                element={
                    <Suspense fallback={<>...</>}>
                        <ArticlesList />
                    </Suspense>
                }
            />
        )}
        {isListNewsEnable && (
            <Route
                path="/articles/:id"
                element={
                    <Suspense fallback={<>...</>}>
                        <ArticleView />
                    </Suspense>
                }
            />
        )}
        <Route
            path="/news/:id"
            element={
                <Suspense fallback={<>...</>}>
                    <NewsView />
                </Suspense>
            }
        />
        <Route
            path="sandbox"
            element={
                <Suspense fallback={<>...</>}>
                    <Sandbox />
                </Suspense>
            }
        />
        <Route
            path="/donate"
            element={
                <Suspense fallback={<>...</>}>
                    <Donate />
                </Suspense>
            }
        />
        <Route
            path="/donate/:id"
            element={
                <Suspense fallback={<>...</>}>
                    <DonateVotes />
                </Suspense>
            }
        />
        {isClubsRedeemEnabled && (
            <>
                <Route
                    path="/promo"
                    element={
                        <Suspense fallback={<>...</>}>
                            <ClubVoucherPurchase />
                        </Suspense>
                    }
                />
                <Route
                    path="/clubs/redeem/:id"
                    element={
                        <Suspense fallback={<>...</>}>
                            <ClubVoucherRedeem />
                        </Suspense>
                    }
                />
            </>
        )}

        {isClubsEnable && <Route path="/clubs" element={<PageNotFind />} />}

        <Route path="*" element={<PageNotFind />} />
    </Routes>
);
